"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { useQueryClient } from "@tanstack/react-query"
import { PricingTrialHeader } from "./pricing-trial-header"
import { TrialCreditsBadge } from "@/components/trial/trial-credits-badge"
import { TrialFeaturesSection } from "@/components/trial/trial-features-section"
import { TrialContinueButton } from "@/components/trial/trial-continue-button"
import { useTrialContextSafe } from "@/components/trial/trial-context"
import { useUserDataContextSafe } from "@/components/navigation/contexts/user-data-context"
import { startFreeTrial } from "@/services/billing"
import { TRIAL_JUST_ACTIVATED_KEY } from "@/constants/trial"
import type { Subscription } from "@/app/types/Subscription"

interface PricingTrialSectionProps {
    publicMode?: boolean
    onTrialStart?: () => void
    onPlanSelectForTrial?: () => void
    currentSubscription?: Subscription
}

export function PricingTrialSection({ publicMode, onTrialStart, onPlanSelectForTrial, currentSubscription }: PricingTrialSectionProps) {
    const trialContext = useTrialContextSafe()
    const userData = useUserDataContextSafe()
    const router = useRouter()
    const queryClient = useQueryClient()
    const [isLoading, setIsLoading] = useState(false)
    const [trialError, setTrialError] = useState<string | null>(null)

    const hasActiveSubscription = currentSubscription?.status === 'active' || currentSubscription?.status === 'trialing'
    if (!publicMode && (userData?.isUserDetailsLoading || !trialContext?.eligibleForTrial || hasActiveSubscription)) return null

    const handleTrialStart = async () => {
        if (publicMode && onPlanSelectForTrial) {
            onPlanSelectForTrial()
            return
        }
        if (onTrialStart) {
            onTrialStart()
            return
        }
        if (trialContext) {
            setIsLoading(true)
            setTrialError(null)

            try {
                const result = await startFreeTrial()
                if (!result.success) {
                    console.error('Error activating trial:', result.error)
                    setTrialError(result.error || 'Failed to start trial. Please try again.')
                    return
                }

                sessionStorage.setItem(TRIAL_JUST_ACTIVATED_KEY, 'true')

                await new Promise(resolve => setTimeout(resolve, 1000))

                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['userDetails'] }),
                    queryClient.invalidateQueries({ queryKey: ['userTeams'] }),
                    queryClient.invalidateQueries({ queryKey: ['totalBalance'] }),
                    queryClient.invalidateQueries({ queryKey: ['personalBalance'] }),
                    queryClient.invalidateQueries({ queryKey: ['teamCredits'] }),
                ])

                await Promise.all([
                    queryClient.refetchQueries({ queryKey: ['userDetails'] }),
                    queryClient.refetchQueries({ queryKey: ['userTeams'] }),
                    queryClient.refetchQueries({ queryKey: ['teamCredits'] }),
                ])

                try {
                    await router.refresh()
                } catch (err) {
                    console.error('Refresh failed after trial activation:', err)
                }
            } catch (error: any) {
                console.error('Error activating trial:', error)
                setTrialError(error?.message || 'Failed to start trial. Please try again.')
            } finally {
                setIsLoading(false)
            }
        }
    }
    return (
        <div className='mt-12 mx-4 bg-white border border-main rounded-2xl overflow-hidden'>
            {!publicMode && (
                <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 sm:px-8 py-3 bg-soft-blue/03'>
                    <PricingTrialHeader
                        title="Start your 7 days free trial"
                        subtitle="Full advanced version"
                    />
                    <TrialCreditsBadge className='mt-0' contentClassName='border border-orange/02' />
                </div>
            )}

            <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-6 px-4 sm:px-8'>
                {publicMode ? (
                    <PricingTrialHeader
                        title="Start your 7-days free trial"
                        subtitle="No credit card needed"
                    />
                ) : (
                    <TrialFeaturesSection
                        containerClassName='mt-0'
                        textClassName='text-left'
                        featuresContainerClassName='mt-6 sm:ml-8 mr-0 '
                    />
                )}

                <div className='flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto'>
                    <TrialContinueButton
                        onClick={handleTrialStart}
                        title={isLoading ? 'Starting...' : 'Start 7 days Free Trial'}
                        showDesc={!publicMode}
                        disabled={isLoading}
                    />
                    {trialError && (
                        <p className='text-sm text-red-500'>{trialError}</p>
                    )}
                </div>
            </div>
        </div>
    )
}
